import { MarketCode, Order, Product, ProductSize } from '@/network/graphql.g'
import { ProductWithSize } from '@/types/productWithSize'
import { ImageMode } from '@/utils/imageExtension'
import { getMhubProductData, MhubProductData } from './productManager'

type MHubCartData = {
  products: MhubCartProductData[]
}

type MhubCartProductData = {
  available: number
  list: string
  position: number
  quantity: number
  variant: string
  variantId: number
  isStock: boolean
  pocketPriceWoTax: number
  localPocketPriceWoTax: number
} & MhubProductData

type MhubCartEventProductData = {
  variantId?: string
  variant?: string
  isStock?: boolean
  available?: number
  deliveryTime?: {
    from: number
    to: number
  }
} & MhubProductData

export type MHubCart = {
  cart: MHubCartData
}

type MhubCartEvent = {
  event: string
  action:
    | {
        add: {
          products: MhubCartEventProductData[]
        }
      }
    | {
        remove: {
          products: MhubCartEventProductData[]
        }
      }
}
export const getEmptyMhubCart = (
  imageMode: ImageMode,
  subdomainName: MarketCode
): MHubCart => getMhubCart(null, imageMode, subdomainName as MarketCode)

export const getMhubCart = (
  cart: Order,
  imageMode: ImageMode,
  subdomainName: MarketCode
): MHubCart => {
  const isEurCountry = [MarketCode.Sk, MarketCode.Hr].includes(
    subdomainName as MarketCode
  )
  const roundIfNonEU = (
    shouldRound: boolean,
    value: number | null | undefined
  ) => (value == null ? null : shouldRound ? Math.round(value) : value)
  return {
    cart:
      cart?.products?.length > 0
        ? {
            products:
              cart?.products?.map((item, index) => ({
                ...getMhubProductData(item.product, imageMode),
                fullPrice: item?.product?.MHubProperties?.fullPrice,
                pocketPrice:
                  item?.discountPriceEur ??
                  item?.product?.MHubProperties?.pocketPrice,
                localFullPrice: item?.product?.MHubProperties?.localFullPrice,
                localPocketPrice:
                  item?.discountPrice ??
                  item?.product?.MHubProperties?.localPocketPrice,
                pocketPriceWoTax:
                  item.discountPriceEurWithoutVat == null
                    ? item.product?.MHubProperties?.price
                    : item.discountPriceEurWithoutVat,
                localPocketPriceWoTax:
                  item.discountPriceWithoutVat == null
                    ? item.product?.MHubProperties?.localPrice
                    : roundIfNonEU(!isEurCountry, item.discountPriceWithoutVat),
                ...{
                  available: item.isStock ? item.availability : 0,
                  list: 'cart.widget',
                  position: index + 1,
                  quantity: item.count,
                  variant: item.size?.value,
                  variantId: item.size?.id ? +item.size.id : null,
                  isStock: item.isStock
                }
              })) ?? []
          }
        : null
  }
}

const getMhubCartEventProductData = (
  product: Product,
  size: ProductSize,
  imageMode: ImageMode,
  cart: Order
): MhubCartEventProductData => {
  const item = cart.products.find((item) => {
    return item.product.id === product.id && item.size.id === size.id
  })

  return {
    ...getMhubProductData(product, imageMode),
    ...{
      variantId: size?.id,
      variant: size?.value,
      isStock: size?.isStock,
      available: size?.available ?? 0,
      quantity: item?.count ?? 0,
      deliveryTime: {
        from: size?.delivery?.lowest,
        to: size?.delivery?.highest
      }
    }
  }
}

export const getMhubCartAddEvent = (
  product: Product,
  size: ProductSize,
  imageMode: ImageMode,
  cart: Order
): MhubCartEvent => ({
  event: 'action.add.products',
  action: {
    add: {
      products: [getMhubCartEventProductData(product, size, imageMode, cart)]
    }
  }
})

export const getMhubCartAddMultipleEvent = (
  products: ProductWithSize[],
  imageMode: ImageMode,
  cart: Order
): MhubCartEvent => ({
  event: 'action.add.products',
  action: {
    add: {
      products: products.map((product) =>
        getMhubCartEventProductData(product, product.size, imageMode, cart)
      )
    }
  }
})

export const getMhubCartRemoveEvent = (
  data: { product: Product; size: ProductSize }[],
  imageMode: ImageMode,
  cart: Order
): MhubCartEvent => ({
  event: 'action.remove.products',
  action: {
    remove: {
      products: data.map(({ product, size }) =>
        getMhubCartEventProductData(product, size, imageMode, cart)
      )
    }
  }
})
